import React from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

interface ImportProps {
  buttonStyles: object;
}

const Import: React.FC<ImportProps> = ({ buttonStyles }) => {
  const navigate = useNavigate();

  // Dialog and file-reading states
  const [importDialogOpen, setImportDialogOpen] = React.useState(false);
  const [importData, setImportData] = React.useState<any>(null);
  const [importError, setImportError] = React.useState<string | null>(null);

  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  // Trigger hidden file input when button is clicked
  const handleOpenFileDialog = () => {
    fileInputRef.current?.click();
  };

  // Validate JSON structure
  const isValidMapData = (data: any) => {
    if (!data || typeof data !== "object") return false;
    // Adjust if your map data has more/less required fields
    const requiredKeys = [
      "minRow",
      "maxRow",
      "minCol",
      "maxCol",
      "tilesL0",
      "tilesL1",
      "tilesL2",
      "tilesL3",
    ];
    return requiredKeys.every((k) => Object.prototype.hasOwnProperty.call(data, k));
  };

  // Handle file selection
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Only allow .json extension
    if (!file.name.toLowerCase().endsWith(".json")) {
      setImportData(null);
      setImportError("Invalid file type. Please select a .json file.");
      setImportDialogOpen(true);
      e.target.value = ""; // reset file input
      return;
    }

    try {
      const text = await file.text();
      const parsed = JSON.parse(text);

      // Validate data
      if (!isValidMapData(parsed)) {
        setImportData(null);
        setImportError("Invalid file structure. Not a valid map file.");
      } else {
        setImportData(parsed);
        setImportError(null);
      }
    } catch (err) {
      console.error(err);
      setImportData(null);
      setImportError("Error parsing file. Ensure it's valid JSON.");
    }

    // Show confirmation (or error) dialog
    setImportDialogOpen(true);
    // Clear out file input
    e.target.value = "";
  };

  const handleImportConfirm = () => {
    if (!importData) {
      // If there's no data, likely an error; just close
      setImportDialogOpen(false);
      return;
    }
    // Overwrite 'map-active' in localStorage
    localStorage.removeItem("map-active");
    localStorage.setItem("map-active", JSON.stringify(importData));

    setImportDialogOpen(false);
    // Navigate to map creation
    navigate("/create");
  };

  const handleImportCancel = () => {
    setImportDialogOpen(false);
    setImportData(null);
    setImportError(null);
  };

  return (
    <>
      {/* The Button that triggers a hidden file input */}
      <Button
        variant="contained"
        sx={buttonStyles}
        onClick={handleOpenFileDialog}
      >
        Import Map
      </Button>

      {/* Hidden file input for .json files */}
      <input
        type="file"
        accept=".json"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      {/* Dialog for Confirmation or Error */}
      <Dialog open={importDialogOpen} onClose={handleImportCancel}>
        <DialogTitle>
          {importError ? "Import Error" : "Confirm Import"}
        </DialogTitle>
        <DialogContent>
          {importError ? (
            <Typography color="error">{importError}</Typography>
          ) : (
            <Typography>
              Importing a map file will overwrite your current map. Continue?
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleImportCancel} color="primary">
            {importError ? "Close" : "Cancel"}
          </Button>
          {!importError && (
            <Button onClick={handleImportConfirm} color="primary" autoFocus>
              Confirm
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Import;